import React, { forwardRef, Ref, useEffect, useImperativeHandle, useState } from 'react';
import { forEach, get, isEmpty, map, pick } from '@libs/shared';
import {
    Alert,
    Button,
    Check,
    CheckCircleFilled,
    ExclamationCircleFilled,
    Message,
    Spin,
    Tooltip
} from '@fx-ui/fine-design';
import classNames from 'classnames';
import MsgTargetEditPane from '../msg-target-edit-pane';
import { MessageChannel } from '../../interfaces';
import { getRequestUrl, hasFunctionContactPhone, post } from '../../../../utils';
import { authModel } from '../../../../models';
import { platformIntl, API } from '../../../../constants';

interface IMsgTargetConfigProps {
    value?: Array<any>;
    type?: MessageChannel;
    multiSelect?: boolean;
    onChange?: (selected: Array<any>) => void;
    onGetTargets?: (selected: Array<any>) => void;
    height?: number;
}

const MAX_COUNT = 100;

const MsgTargetConfig: React.ForwardRefRenderFunction<HTMLDivElement, IMsgTargetConfigProps> = (
    props: IMsgTargetConfigProps,
    ref: Ref<any>
) => {
    const {
        value = [],
        type = 'sms',
        multiSelect = true,
        onChange,
        onGetTargets,
        height = 370
    } = props;
    const [targets, setTargets] = useState<Array<any>>([]);
    const [visible, setVisible] = useState(false);
    const [selected, setSelected] = useState<Array<any>>([]);
    const [loading, setLoading] = useState(false);
    const [current, setCurrent] = useState<any>(null);
    const [showAlert, setShowAlert] = useState(false);

    useImperativeHandle(ref, () => {
        return {
            addTarget,
            getNameMap
        };
    });
    const getNameMap = () => {
        const map: any = {};
        forEach(targets, (target) => {
            map[target._id] = pick(target, ['name', 'email_info', 'phone_info']);
        });
        return map;
    };
    const addTarget = () => {
        if (targets.length >= MAX_COUNT) {
            Message.add({
                messageType: 'toast',
                type: 'warning',
                message: platformIntl.get('msg.target.add.max', { maxNum: MAX_COUNT })
            });
        } else {
            setCurrent(null);
            setVisible(true);
        }
    };
    useEffect(() => {
        getTargets();
    }, []);
    const getTargets = () => {
        setLoading(true);
        post(getRequestUrl(API.message.list_receiver, authModel.tenantId)).then((res) => {
            setLoading(false);
            const receivers = get(res, 'data.receivers', []);
            setTargets(receivers);
            getSelected(receivers);
        });
    };
    const getSelected = (receivers: Array<any>) => {
        const temp: Array<any> = [];
        forEach(value, (val) => {
            forEach(receivers, (receiver) => {
                if (receiver._id === val) {
                    temp.push(val);
                }
            });
        });
        setSelected(temp);
        onGetTargets && onGetTargets(temp);
    };
    const getAllCanSelect = () => {
        const temp: Array<any> = [];
        forEach(targets, (item) => {
            if (!getDisabled(item)) {
                temp.push(item._id);
            }
        });
        return temp;
    };
    const getDisabled = (item: any) => {
        if (type === 'sms') {
            return isEmpty(item.phone_info && item.phone_info.phone);
        } else {
            return isEmpty(item.email_info && item.email_info.email);
        }
    };
    const isSelectAll = () => {
        return selected.length === getAllCanSelect().length && selected.length > 0;
    };
    const switchSelectAll = () => {
        if (isSelectAll()) {
            setSelected([]);
            onChange && onChange([]);
        } else {
            const temp = getAllCanSelect();
            setSelected(temp);
            onChange && onChange(temp);
        }
    };
    const isSelectItem = (item: any) => {
        return selected.indexOf(item._id) > -1;
    };
    const switchSelectItem = (item: any) => {
        const arr = [...selected];
        const idx = arr.indexOf(item._id);
        if (idx > -1) {
            arr.splice(idx, 1);
        } else {
            arr.push(item._id);
        }
        setSelected(arr);
        onChange && onChange(arr);
    };
    const sendValidation = (targetId: string, channel: string) => {
        post(getRequestUrl(API.message.validate_receiver, authModel.tenantId), {
            _id: targetId,
            channel: channel
        }).then(() => {
            let msg = '';
            switch (channel) {
                case 'email':
                    msg = platformIntl.get('msg.validate.send.email');
                    break;
                case 'phone':
                    msg = platformIntl.get('msg.validate.send.phone');
                    break;
                default:
                    break;
            }
            if (msg) {
                Message.add({
                    messageType: 'toast',
                    type: 'success',
                    message: msg
                });
            }
        });
    };
    const getIndex = () => {
        let index = -1;
        if (current) {
            forEach(targets, (target, i) => {
                if (target._id === current._id) {
                    index = i;
                    return false;
                }
            });
        }
        return index;
    };
    const addOrUpdateTarget = (val: any) => {
        if (!isEmpty(val)) {
            const arr = [...targets];
            const index = getIndex();
            if (index > -1) {
                arr.splice(index, 1, val);
            } else {
                arr.unshift(val);
            }
            setTargets(arr);
        }
    };
    const removeTarget = () => {
        const arr = [...targets];
        const index = getIndex();
        if (index > -1) {
            arr.splice(index, 1);
        }
        setTargets(arr);
    };
    const renderHeader = () => {
        return (
            <div className="config-header">
                {multiSelect && (
                    <Check
                        className="check-item"
                        checked={isSelectAll()}
                        onChange={switchSelectAll}
                    />
                )}
                <div className="col-1">{platformIntl.get('name')}</div>
                {hasFunctionContactPhone() && (
                    <div className="col-2">{platformIntl.get('phone')}</div>
                )}
                <div className="col-3">{platformIntl.get('email')}</div>
            </div>
        );
    };
    const renderContent = () => {
        return (
            <div className={classNames('config-content', { empty: isEmpty(targets) })}>
                {loading ? (
                    <Spin />
                ) : isEmpty(targets) ? (
                    <span className="empty-text">{platformIntl.get('msg.target.empty')}</span>
                ) : (
                    map(targets, (item, index) => {
                        return (
                            <div className="target-item" key={index}>
                                {multiSelect && (
                                    <Check
                                        className="check-item"
                                        disabled={getDisabled(item)}
                                        checked={isSelectItem(item)}
                                        onChange={() => switchSelectItem(item)}
                                    />
                                )}
                                <div className="col-1 name">
                                    <div className="name-info">{item.name}</div>
                                </div>
                                {hasFunctionContactPhone() && (
                                    <div className="col-2 phone">
                                        {item.phone_info && (
                                            <>
                                                <div className="phone-info">
                                                    {item.phone_info.phone}
                                                </div>
                                                {item.phone_info.is_verified ? (
                                                    <CheckCircleFilled className="phone-icon verified" />
                                                ) : (
                                                    <>
                                                        <Tooltip
                                                            effect="light"
                                                            content={platformIntl.get(
                                                                'msg.target.phone.unverified'
                                                            )}
                                                            placement="top"
                                                        >
                                                            <ExclamationCircleFilled className="phone-icon" />
                                                        </Tooltip>
                                                        <Button
                                                            className="phone-btn"
                                                            type="text"
                                                            size="small"
                                                            onClick={() =>
                                                                sendValidation(item._id, 'phone')
                                                            }
                                                        >
                                                            {platformIntl.get('msg.validate')}
                                                        </Button>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                                <div className="col-3 email">
                                    {item.email_info && (
                                        <>
                                            <div className="email-info">
                                                {item.email_info.email}
                                            </div>
                                            {item.email_info.is_verified ? (
                                                <CheckCircleFilled className="email-icon verified" />
                                            ) : (
                                                <>
                                                    <Tooltip
                                                        effect="light"
                                                        content={platformIntl.get(
                                                            'msg.target.email.unverified'
                                                        )}
                                                        placement="top"
                                                    >
                                                        <ExclamationCircleFilled className="email-icon" />
                                                    </Tooltip>
                                                    <Button
                                                        className="email-btn"
                                                        type="text"
                                                        size="small"
                                                        onClick={() =>
                                                            sendValidation(item._id, 'email')
                                                        }
                                                    >
                                                        {platformIntl.get('msg.validate')}
                                                    </Button>
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        );
    };
    return (
        <>
            <div className="fx-msg-target-config" style={{ height: height }}>
                {renderHeader()}
                {renderContent()}
            </div>
            <MsgTargetEditPane
                value={current}
                visible={visible}
                onCancel={() => setVisible(false)}
                onOk={(val) => {
                    addOrUpdateTarget(val);
                    setVisible(false);
                }}
            />
            <Alert
                type="error"
                title={platformIntl.get('msg.target.delete.title')}
                message={platformIntl.get('msg.target.delete.content')}
                visible={showAlert}
                onCancel={() => setShowAlert(false)}
                onOk={() => {
                    setShowAlert(false);
                    post(getRequestUrl(API.message.delete_receiver, authModel.tenantId), {
                        _id: current._id
                    }).then(() => removeTarget());
                }}
            />
        </>
    );
};

export default forwardRef(MsgTargetConfig);
